/**
 * Permission catalog — derived view over `shared/permission-records.ts`
 * (Tech Debt I, Task #1726).
 *
 * `PERMISSION_CATALOG` and `NON_DELEGATABLE_PERMISSIONS` are projected
 * straight from `PERMISSION_RECORDS`. The records file is the single
 * source of truth for permission metadata (label, description, module,
 * risk level, requiresFeature, default-role assignment, non-delegatable
 * flag). Do NOT add a permission directly to the catalog — add it to
 * `permission-records.ts` and it will appear here automatically.
 *
 * Consumers (server route guards, governance UI, validators) keep their
 * existing import paths; only the implementation is consolidated.
 */
import { PERMISSION_RECORDS, type PermissionRecord } from "./permission-records";

export type RiskLevel = "low" | "medium" | "high" | "critical";

export interface PermissionMeta {
  key: string;
  label: string;
  description: string;
  module: string;
  riskLevel: RiskLevel;
  /**
   * L1 FEATURE_CATALOG dot-notation key that must be enabled for this permission
   * to be usable (checked in requirePermission({fullResolution:true}) and in
   * PERMISSION_FEATURE_MAP for session-level stripping).
   *
   * IMPORTANT: These must be dot-notation catalog keys (e.g. "operations.ptw.basicPermits"),
   * NOT legacy module keys (e.g. "ptw").
   */
  requiresFeature?: string;
}

function toMeta(p: PermissionRecord): PermissionMeta {
  const meta: PermissionMeta = {
    key: p.key,
    label: p.label,
    description: p.description,
    module: p.module,
    riskLevel: p.riskLevel,
  };
  if (p.requiresFeature) meta.requiresFeature = p.requiresFeature;
  return meta;
}

export const PERMISSION_CATALOG: PermissionMeta[] = PERMISSION_RECORDS.map(toMeta);

export const PERMISSION_CATALOG_BY_KEY: Record<string, PermissionMeta> = Object.fromEntries(
  PERMISSION_CATALOG.map((p) => [p.key, p]),
);

export const PERMISSION_CATALOG_MODULES: string[] = [
  ...new Set(PERMISSION_CATALOG.map((p) => p.module)),
];

export const MODULE_LABELS: Record<string, string> = {
  dashboard: "Dashboard",
  access: "Access Control",
  ptw: "Permit to Work",
  evidence: "Evidence",
  credentials: "Credentials",
  safety: "Safety & Incidents",
  sites: "Sites & Areas",
  companies: "Company Management",
  workforce: "Workforce",
  equipment: "Equipment",
  timeattendance: "Time & Attendance",
  documents: "Documents",
  readiness: "Workforce Readiness",
  operations: "Operations",
  onboarding: "Onboarding",
  administration: "Administration",
  ncr: "Non-Conformance Reports",
  platform: "Platform (Super Admin)",
  share: "Share Spaces",
};

export const RISK_LEVEL_LABELS: Record<string, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
  critical: "Critical",
};

export const RISK_LEVEL_COLORS: Record<string, string> = {
  low: "bg-green-100 text-green-800",
  medium: "bg-yellow-100 text-yellow-800",
  high: "bg-orange-100 text-orange-800",
  critical: "bg-red-100 text-red-800",
};

/**
 * `NON_DELEGATABLE_PERMISSIONS` is derived from
 * `PERMISSION_RECORDS[*].nonDelegatable`. These keys can never be assigned
 * to a custom role — only to built-in roles whose default grants include
 * them. Used by the role editor (admin UI) and the role-permission overrides
 * cache to refuse delegation.
 */
export const NON_DELEGATABLE_PERMISSIONS = new Set<string>(
  PERMISSION_RECORDS.filter((p) => p.nonDelegatable).map((p) => p.key),
);

export function getPermissionMeta(key: string): PermissionMeta | undefined {
  return PERMISSION_CATALOG_BY_KEY[key];
}

export function getPermissionsByModule(module: string): PermissionMeta[] {
  return PERMISSION_CATALOG.filter((p) => p.module === module);
}

export function isNonDelegatable(key: string): boolean {
  return NON_DELEGATABLE_PERMISSIONS.has(key);
}
